<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import ReorderableTable from '@/components/ReorderableTable.vue';
import type { ProjectDto } from '@/services/types/api.types';
import { useAuthStore, useCommunityStore, useProjectStore, useProjectTemplateStore } from '@/stores';
import CreateProjectDialog from '@/components/dialogs/CreateProjectDialog.vue';
import { Constants } from '@/constants';
import { pageAfterDeletion } from '@/composables/pagination-utils';
import CreateProjectTemplateDialog from '@/components/dialogs/CreateProjectTemplateDialog.vue';
import ProjectTemplateList from '@/components/ProjectTemplateList.vue';
import RoleVisibilityGuard from '@/components/RoleVisibilityGuard.vue';
import { getTranslation } from '@/composables/data-display-utils';
import { confirmDelete } from '@/helpers/dialogs.helper';

const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

const projectStore = useProjectStore();
const projectTemplateStore = useProjectTemplateStore();
projectStore.fetchAll();
const communityStore = useCommunityStore();
const { community } = storeToRefs(communityStore);
const { isLoading, pagination, projectListItems } = storeToRefs(projectStore);

const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: ProjectDto) => getTranslation(row, 'title', row.language_enabled),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: ProjectDto) => getStatus(row),
    align: 'left',
  },
  {
    name: 'visibility',
    label: t('global.label.visibility'),
    field: (row: ProjectDto) => getVisibility(row),
    align: 'left',
  },
]);

const actionMenuItems = computed(() => {
  if (isAdmin()) {
    return [
      { action: 'create-template', label: t('project.create_template') },
      { action: 'delete-row', label: t('global.label.delete') },
    ];
  }
  return [];
});

function getDefaultLanguageEnabled() {
  return [community.value?.default_language ?? Constants.DefaultLocale];
}

function getStatus(project: ProjectDto) {
  const statuses = projectStore.getProjectStatuses();
  return statuses.find((s) => s.value === project.status).label;
}

function getVisibility(project: ProjectDto) {
  const visibilities = projectStore.getProjectVisibilities();
  return visibilities.find((s) => s.value === project.visibility).label;
}

const handleRowClick = (row: ProjectDto) => {
  router.push({ name: 'project_details', params: { id: row.id } });
};

const handleAddRow = () => {
  const project: ProjectDto = projectStore.create({ languageEnabled: getDefaultLanguageEnabled() });
  $q.dialog({
    component: CreateProjectDialog,
    componentProps: {
      project,
      languageEnabled: getDefaultLanguageEnabled(),
    },
  });
};

const handleDeleteRow = (row: ProjectDto, q: string | null = null) => {
  if (!row.id) {
    console.warn('Row does not have an id');
    return;
  }

  confirmDelete(t('project.name')).onOk(() => {
    const page = pageAfterDeletion(pagination.value);
    projectStore.delete(row.id!, page, q);
  });
};

const handleActions = async (action: any) => {
  switch (action.action) {
    case 'create-template':
      const projectId = action.payload.row.id;
      await projectStore.fetchById(projectId);
      const project = projectStore.getItem(projectId);
      $q.dialog({
        component: CreateProjectTemplateDialog,
        componentProps: {
          projectTemplate: projectTemplateStore.create({
            title: project?.title,
            description: project?.description,
            cloneFromId: project?.id,
            languageEnabled: project?.language_enabled ?? getDefaultLanguageEnabled(),
          }),
          languageEnabled: project?.language_enabled ?? getDefaultLanguageEnabled(),
        },
      });
      break;
  }
};

const fetchProjects = async (request: any) => {
  await projectStore.fetchAll(request.page, request.rowsPerPage, request.q);
};

function isAdmin() {
  return useAuthStore().role === Constants.User.Role.ADMIN;
}
</script>

<template>
  <q-page class="projects-list">
    <h1 class="title">{{ t('project.projects') }}</h1>
    <reorderable-table
      :rows="projectListItems"
      :columns="columns"
      :loading="isLoading"
      itemKey="id"
      :disableAddButton="!isAdmin()"
      :disableDeleteButton="true"
      :disableOrderable="true"
      :pagination-enabled="true"
      :pagination="pagination"
      :search-enabled="true"
      :action-menu-items="actionMenuItems"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @request="fetchProjects"
      @actions="handleActions"
    />
    <role-visibility-guard :rolesAllowed="[Constants.User.Role.ADMIN]">
      <q-separator spaced="xl" />
      <project-template-list />
    </role-visibility-guard>
  </q-page>
</template>
<style lang="scss">
.projects-list {
  padding: 16px;

  .title {
    margin: 0 10px !important;
  }
}
</style>
